import i18n from '../i18n';

/**
 * Humanize snake_case / kebab-case codes for customer UI fallback.
 * Never prefer raw technical codes as the primary label.
 */
export function humanizeCode(value) {
  if (value == null || value === '') {
    return '';
  }

  return String(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function translateOrFallback(key, fallback) {
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  return fallback;
}

function warnUnknownInDev(kind, value) {
  if (process.env.NODE_ENV === 'development' && value) {
    // eslint-disable-next-line no-console
    console.warn(`[customerPortalLabels] Unknown ${kind}:`, value);
  }
}

export function formatBillingInterval(value) {
  const normalized = String(value || '').toLowerCase();
  const map = {
    monthly: 'customerPortal:billing.monthly',
    yearly: 'customerPortal:billing.yearly',
    annual: 'customerPortal:billing.yearly',
    trial: 'customerPortal:billing.trial',
  };
  if (map[normalized]) {
    return i18n.t(map[normalized]);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('billingInterval', value);
  return humanizeCode(value);
}

export function formatApplicationReadiness(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:applicationReadiness.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (!normalized || normalized === 'legacy') {
    return '';
  }
  warnUnknownInDev('applicationReadiness', value);
  return humanizeCode(value);
}

export function formatTenantStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:tenantStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:subscription.${normalized}`)) {
    return i18n.t(`statuses:subscription.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('tenantStatus', value);
  return humanizeCode(value);
}

export function formatAccessGateStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:accessStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:tenant_access.${normalized}`)) {
    return i18n.t(`statuses:tenant_access.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('accessStatus', value);
  return humanizeCode(value);
}

export function formatProvisioningStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:provisioningStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:provisioning_status.${normalized}`)) {
    return i18n.t(`statuses:provisioning_status.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('provisioningStatus', value);
  return humanizeCode(value);
}

export function formatSchemaStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:schemaStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:schema_plan.${normalized}`)) {
    return i18n.t(`statuses:schema_plan.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('schemaStatus', value);
  return humanizeCode(value);
}

export function formatSetupStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:setupStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:setup_session.${normalized}`)) {
    return i18n.t(`statuses:setup_session.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('setupStatus', value);
  return humanizeCode(value);
}

export function formatUserType(value) {
  const normalized = String(value || '').toLowerCase();
  const map = {
    customer: 'customerPortal:userTypes.customer',
    system: 'customerPortal:userTypes.system',
    internal: 'customerPortal:userTypes.system',
  };
  if (map[normalized]) {
    return i18n.t(map[normalized]);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('userType', value);
  return humanizeCode(value);
}

export function formatCustomerRole(value) {
  const normalized = String(value || '').toLowerCase();
  const map = {
    customer_admin: 'customerPortal:roles.customerAdmin',
    customer_user: 'customerPortal:roles.customerUser',
  };
  if (map[normalized]) {
    return i18n.t(map[normalized]);
  }
  if (i18n.exists(`customers:role_${normalized}`)) {
    return i18n.t(`customers:role_${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('customerRole', value);
  return humanizeCode(value);
}

export function formatMembershipStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:membershipStatus.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (!normalized) {
    return '';
  }
  return humanizeCode(value);
}

const MODULE_ALIASES = {
  global_settings: 'globalSettings',
  quality_control: 'quality',
  quality: 'quality',
  maintenance: 'maintenance',
  support: 'support',
  schooling: 'training',
  training: 'training',
  checklists: 'checklists',
  'check-list': 'checklists',
  company_portal: 'companyPortal',
  'company-portal': 'companyPortal',
  priority: 'priority',
  priority_orders: 'priority',
  paint_shop: 'paintShop',
  'paint-shop': 'paintShop',
  calibrations: 'calibrations',
  crm: 'crm',
};

export function formatModuleCode(value) {
  if (!value) {
    return '';
  }
  const normalized = String(value).toLowerCase().replace(/-/g, '_');
  const alias = MODULE_ALIASES[normalized] || MODULE_ALIASES[String(value).toLowerCase()];
  if (alias) {
    const key = `customerPortal:modules.${alias}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }
  warnUnknownInDev('moduleCode', value);
  return humanizeCode(value);
}

const CAPABILITY_ALIASES = {
  otk_email_reports: 'otkEmailReports',
  otk_company_documentation: 'otkCompanyDocumentation',
  maintenance_templates: 'maintenanceTemplates',
  maintenance_plans: 'maintenancePlans',
  departments: 'departments',
  employees: 'employees',
  users: 'users',
  roles: 'roles',
  machines: 'machines',
  machine_types: 'machineTypes',
  business_partners: 'businessPartners',
  units: 'units',
};

export function formatCapabilityCode(value) {
  if (!value) {
    return '';
  }
  const normalized = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  const alias = CAPABILITY_ALIASES[normalized];
  if (alias) {
    const key = `customerPortal:capabilities.${alias}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }
  warnUnknownInDev('capabilityCode', value);
  return humanizeCode(value);
}

/**
 * Prefer display name; if it looks like a raw code, translate via formatModuleCode.
 */
export function formatModuleLabel(moduleOrName, moduleCode) {
  const code = moduleCode || moduleOrName?.module_code || moduleOrName?.canonical_code;
  const name = typeof moduleOrName === 'string'
    ? moduleOrName
    : (moduleOrName?.name
      || moduleOrName?.title
      || moduleOrName?.customer_name
      || moduleOrName?.public_label);

  if (name && code && String(name).toLowerCase().replace(/-/g, '_') === String(code).toLowerCase().replace(/-/g, '_')) {
    return formatModuleCode(code);
  }
  if (name && !/[a-z]+_[a-z]+/i.test(name) && name !== code) {
    return name;
  }
  if (code) {
    return formatModuleCode(code);
  }
  if (name) {
    return /_/.test(name) ? formatCapabilityCode(name) : name;
  }
  return '';
}

const INTERNAL_MESSAGE_PATTERNS = [
  /missing setup profiles/i,
  /test seed/i,
  /schema runner/i,
  /migration/i,
  /tenant db/i,
  /tenant database/i,
  /database name/i,
  /endpoint disabled/i,
  /sms reporting/i,
  /sms-relevant modules/i,
  /permission gap/i,
  /dual-flow/i,
  /mafertapp/i,
  /s3_prefix/i,
  /provisioning test seed/i,
  /real mafertapp seed/i,
  /required setup item group/i,
  /documented as missing/i,
  /completion tracking deferred/i,
  /blueprint/i,
];

export function isInternalReadinessMessage(message) {
  if (!message || typeof message !== 'string') {
    return true;
  }
  return INTERNAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

export function formatModuleSource(value) {
  const normalized = String(value || '').toLowerCase().replace(/-/g, '_');
  const map = {
    core: 'customerPortal:moduleSources.core',
    subscription: 'customerPortal:moduleSources.subscription',
    registration: 'customerPortal:moduleSources.registration',
    override: 'customerPortal:moduleSources.override',
    override_required: 'customerPortal:moduleSources.overrideRequired',
    not_subscribed: 'customerPortal:moduleSources.notSubscribed',
    plan: 'customerPortal:moduleSources.subscription',
    paid_module: 'customerPortal:moduleSources.paidModule',
    support_dual_flow: 'customerPortal:moduleSources.supportDualFlow',
    future_override: 'customerPortal:moduleSources.futureOverride',
    functional_capabilities: 'customerPortal:moduleSources.functionalCapabilities',
    mafertapp_functional_capabilities: 'customerPortal:moduleSources.functionalCapabilities',
    functional_capability: 'customerPortal:moduleSources.functionalCapability',
    permission_mapping: 'customerPortal:moduleSources.permissionMapping',
    mafertapp_module_permission_mappings: 'customerPortal:moduleSources.permissionMapping',
    setup_profile: 'customerPortal:moduleSources.setupProfile',
    seed_policy: 'customerPortal:moduleSources.seedPolicy',
    menu_page: 'customerPortal:moduleSources.menuPage',
    registry_page: 'customerPortal:moduleSources.registryPage',
    support_flow: 'customerPortal:moduleSources.supportFlow',
    scheduled_job: 'customerPortal:moduleSources.scheduledJob',
    global_mandatory: 'customerPortal:moduleSources.globalMandatory',
    module_selected: 'customerPortal:moduleSources.moduleSelected',
    always_seeded: 'customerPortal:moduleSources.alwaysSeeded',
    deferred: 'customerPortal:moduleSources.deferred',
    future: 'customerPortal:moduleSources.future',
  };
  if (map[normalized]) {
    return i18n.t(map[normalized]);
  }
  if (i18n.exists(`systemLabels:featureTypes.${normalized}`)) {
    return i18n.t(`systemLabels:featureTypes.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('moduleSource', value);
  return humanizeCode(value);
}

export function formatRegistrationSource(value) {
  const normalized = String(value || '').toLowerCase();
  const map = {
    public_register: 'customerPortal:registrationSources.publicRegister',
    public_registration: 'customerPortal:registrationSources.publicRegister',
    trial: 'customerPortal:registrationSources.trial',
    trial_onboarding: 'customerPortal:registrationSources.trial',
    admin: 'customerPortal:registrationSources.admin',
    system: 'customerPortal:registrationSources.admin',
    invite: 'customerPortal:registrationSources.invite',
    invitation: 'customerPortal:registrationSources.invite',
  };
  if (map[normalized]) {
    return i18n.t(map[normalized]);
  }
  if (!normalized) {
    return '';
  }
  warnUnknownInDev('registrationSource', value);
  return humanizeCode(value);
}

export function formatCustomerStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `statuses:customer.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  if (i18n.exists(`statuses:tenant.${normalized}`)) {
    return i18n.t(`statuses:tenant.${normalized}`);
  }
  if (!normalized) {
    return '';
  }
  return humanizeCode(value);
}

/**
 * Localize account-level safe message from key or English fallback text.
 */
export function formatSafeAccountMessage(messageKey, fallbackText) {
  if (messageKey) {
    const key = `customerPortal:safeMessages.${messageKey}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }

  const mapped = formatReadinessMessage(fallbackText);
  if (mapped) {
    return mapped;
  }

  return i18n.t('customerPortal:safeMessages.accountNeedsAttentionBeforeLaunch');
}

/**
 * Map raw readiness messages / codes to customer-safe copy.
 * Returns null when the message should be hidden entirely.
 */
export function formatReadinessMessage(messageOrCode, context = {}) {
  if (!messageOrCode) {
    return null;
  }

  const text = String(messageOrCode);
  const lower = text.toLowerCase();

  if (/needs attention before launch|not ready to launch yet/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.accountNeedsAttentionBeforeLaunch');
  }
  if (/account is ready for the next launch step/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.accountReady');
  }
  if (/mostly ready/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.accountMostlyReady');
  }
  if (/waiting for payment/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.paymentPending');
  }
  if (/required setup item group|documented as missing|setup items are not completed/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.someSetupItemsIncomplete');
  }
  if (/missing setup profiles/i.test(text) || /requires support assistance/i.test(text)) {
    return i18n.t('customerPortal:safeMessages.setupNeedsSupport');
  }
  if (/test seed/i.test(text)) {
    return null;
  }
  if (/schema not started|schema.*not started|database structure preparation/i.test(lower)) {
    return i18n.t('customerPortal:safeMessages.schemaPreparing');
  }
  if (/provisioning not started|environment preparation/i.test(lower)) {
    return i18n.t('customerPortal:safeMessages.environmentPreparing');
  }
  if (/sms notifications will be activated/i.test(lower) || (/sms/i.test(lower) && (/endpoint disabled|reporting/i.test(lower)))) {
    // SMS copy belongs only in the SMS section — never as readiness warning.
    return null;
  }
  if (/sms notifications are an add-on/i.test(lower)) {
    return null;
  }
  if (isInternalReadinessMessage(text)) {
    return null;
  }

  return text;
}

export function filterSafeReadinessWarnings(warnings, context = {}) {
  if (!Array.isArray(warnings)) {
    return [];
  }

  const mapped = warnings
    .map((warning) => formatReadinessMessage(warning, context))
    .filter(Boolean);

  // Prefer support-assistance wording over generic incomplete when both appear.
  const hasSupport = mapped.includes(i18n.t('customerPortal:safeMessages.setupNeedsSupport'));
  const incomplete = i18n.t('customerPortal:safeMessages.someSetupItemsIncomplete');
  const deduped = hasSupport
    ? mapped.filter((item) => item !== incomplete)
    : mapped;

  return [...new Set(deduped)];
}

export function formatPaymentStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const key = `customerPortal:paymentStatus.${normalized}`;
  return translateOrFallback(key, humanizeCode(value));
}
